import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { ADMIN_CPM_CAMPAIGN_TOP_EARNING_CLIPS } from '@/queries/queries';
import type { TopEarningClipsPage, TopEarningClipsQuery } from '@/lib/campaigns/topEarningClips';

export const TOP_EARNING_PAGE_SIZE = 10;
export function useCpmTopEarningClips(campaignId: string) {
  const [requested, setRequested] = useState({ campaignId, page: 1 });
  const page = requested.campaignId === campaignId ? requested.page : 1;
  const [lastLoaded, setLastLoaded] = useState<{
    campaignId: string; page: number; ranking: TopEarningClipsPage;
  }>();
  useEffect(() => {
    setRequested({ campaignId, page: 1 });
    setLastLoaded(undefined);
  }, [campaignId]);
  const { data, loading, error, refetch } = useQuery<TopEarningClipsQuery>(ADMIN_CPM_CAMPAIGN_TOP_EARNING_CLIPS, {
    variables: { campaignId, limit: TOP_EARNING_PAGE_SIZE, offset: (page - 1) * TOP_EARNING_PAGE_SIZE },
    skip: !campaignId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'none',
    notifyOnNetworkStatusChange: true,
  });
  const current = data?.adminCpmCampaignTopEarningClips;
  // Missing data while switching query variables is not an empty ranking.
  // Keep the successful page mounted so the collapsible also stays open.
  useEffect(() => {
    if (loading || error || !current) return;
    const pageCount = Math.max(1, current.pageCount);
    if (page > pageCount) {
      setRequested({ campaignId, page: pageCount });
    } else {
      setLastLoaded({ campaignId, page, ranking: current });
    }
  }, [campaignId, current, error, loading, page]);
  const previous = lastLoaded?.campaignId === campaignId ? lastLoaded : undefined;
  const settled = current && !loading && !error && page <= Math.max(1, current.pageCount);
  const ranking = settled ? current : previous?.ranking;
  return {
    ranking, page: settled ? page : previous?.page ?? 1,
    setPage: (next: number) => setRequested({ campaignId, page: Math.max(1, next) }),
    pageCount: Math.max(1, ranking?.pageCount ?? 1), loading, error, refetch,
  };
}
